import React, { useCallback, useEffect, useRef, useState } from 'react';
import { el } from '../i18n/el';

/** Longest edge cap (px) to keep multi-page JPEG stacks within upload limits while staying sharp. */
const MAX_CAPTURE_EDGE = 2400;
const JPEG_QUALITY = 0.88;

// Multi-page camera: captures the full camera frame (no paper-edge detection / dewarp).
// Props:
//   - open: boolean
//   - onClose: () => void
//   - onComplete: (files: File[]) => void  // JPEG files, one per captured page
export default function CameraScanner({ open, onClose, onComplete }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const thumbsRef = useRef([]);

  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState([]); // { file, previewUrl }
  const [errorMsg, setErrorMsg] = useState('');
  const [flash, setFlash] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const revokeAllThumbs = useCallback(() => {
    thumbsRef.current.forEach((u) => {
      try { URL.revokeObjectURL(u); } catch {}
    });
    thumbsRef.current = [];
  }, []);

  const stopStream = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => { try { t.stop(); } catch {} });
      streamRef.current = null;
    }
  }, []);

  const closeModal = useCallback(() => {
    stopStream();
    revokeAllThumbs();
    setPages([]);
    setReady(false);
    setErrorMsg('');
    onClose && onClose();
  }, [onClose, revokeAllThumbs, stopStream]);

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let canceled = false;
    setLoading(true);
    setErrorMsg('');
    setReady(false);

    (async () => {
      try {
        let stream = null;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
            audio: false,
          });
        } catch (e1) {
          try {
            stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          } catch (e2) {
            throw e2;
          }
        }
        if (canceled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await new Promise((res) => {
          video.onloadedmetadata = () => {
            video.play().then(res, res);
          };
        });
        if (!canceled) setReady(true);
      } catch (e) {
        console.error('Camera init failed:', e);
        setErrorMsg(
          !window.isSecureContext && !(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
            ? 'Camera requires HTTPS or localhost.'
            : (e && (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError'))
              ? 'Camera permission denied. Please allow access in your browser settings.'
              : 'Unable to access camera. Ensure no other app is using it and try again.',
        );
      } finally {
        if (!canceled) setLoading(false);
      }
    })();

    return () => {
      canceled = true;
      stopStream();
    };
  }, [open, stopStream]);

  useEffect(() => () => { revokeAllThumbs(); }, [revokeAllThumbs]);

  const capture = useCallback(async () => {
    if (!ready || capturing) return;
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    setCapturing(true);
    setFlash(true);
    setTimeout(() => setFlash(false), 180);

    try {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      let tw = vw;
      let th = vh;
      const long = Math.max(vw, vh);
      if (long > MAX_CAPTURE_EDGE) {
        const scale = MAX_CAPTURE_EDGE / long;
        tw = Math.round(vw * scale);
        th = Math.round(vh * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = tw;
      canvas.height = th;
      const ctx = canvas.getContext('2d', { alpha: false });
      ctx.drawImage(video, 0, 0, tw, th);

      const blob = await new Promise((resolve, reject) =>
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('capture failed'))),
          'image/jpeg',
          JPEG_QUALITY,
        ),
      );
      const previewUrl = URL.createObjectURL(blob);
      thumbsRef.current.push(previewUrl);
      setPages((prev) => {
        const file = new File([blob], `scan_page_${prev.length + 1}.jpg`, { type: 'image/jpeg' });
        return [...prev, { file, previewUrl }];
      });
    } catch (e) {
      console.error('Capture failed:', e);
    } finally {
      setCapturing(false);
    }
  }, [capturing, ready]);

  const removePage = useCallback((idx) => {
    setPages((prev) => {
      const target = prev[idx];
      if (target?.previewUrl) {
        try { URL.revokeObjectURL(target.previewUrl); } catch {}
        thumbsRef.current = thumbsRef.current.filter((u) => u !== target.previewUrl);
      }
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const finish = useCallback(() => {
    if (!pages.length) return;
    stopStream();
    const files = pages.map((p) => p.file);
    onComplete && onComplete(files);
    revokeAllThumbs();
    setPages([]);
    setReady(false);
    onClose && onClose();
  }, [onClose, onComplete, pages, revokeAllThumbs, stopStream]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black text-white pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-center justify-between px-4 py-2.5 bg-black/85 backdrop-blur-sm">
        <div className="flex items-center gap-3 min-w-0">
          <button
            className="h-10 w-10 -ml-2 rounded-full flex items-center justify-center hover:bg-white/10 active:bg-white/20"
            onClick={closeModal}
            aria-label={el.dashCancel}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold truncate">{el.cameraScanTitle}</h3>
            <p className="text-[11px] text-white/70 truncate hidden sm:block">{el.cameraTipMultiPage}</p>
          </div>
        </div>
        {pages.length > 0 && (
          <span className="inline-flex items-center gap-1.5 bg-white/15 ring-1 ring-white/25 rounded-full px-3 py-1 text-xs">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
            {el.cameraPagesCount}: <strong className="font-semibold">{pages.length}</strong>
          </span>
        )}
      </div>

      {errorMsg && (
        <div className="mx-3 mt-3 text-sm px-3 py-2 rounded-lg bg-amber-100 text-amber-900 border border-amber-200">
          {errorMsg}
        </div>
      )}

      <div className="relative flex-1 overflow-hidden flex items-center justify-center bg-black">
        {!ready && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70 z-20">
            <svg className="animate-spin h-8 w-8 text-white mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
            <span className="text-sm">{loading ? el.cameraStarting : el.cameraReady}</span>
          </div>
        )}
        <video ref={videoRef} className="max-h-full max-w-full object-contain block" playsInline muted />
        {flash && <div className="absolute inset-0 bg-white animate-[ping_0.18s_ease-out] opacity-90 z-20 pointer-events-none" />}
        {ready && pages.length === 0 && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-3 px-3 py-1.5 rounded-full bg-black/60 text-white/90 text-[11px] sm:text-xs ring-1 ring-white/20 z-20 max-w-[90%] text-center">
            {el.cameraTipFullFrame}
          </div>
        )}
      </div>

      <div className={`px-3 pt-3 ${pages.length ? '' : 'opacity-60'}`}>
        {pages.length === 0 ? (
          <div className="text-center text-white/60 text-xs py-2">{el.cameraNoPages}</div>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
            {pages.map((p, idx) => (
              <div key={p.previewUrl} className="relative flex-shrink-0 snap-start">
                <img
                  src={p.previewUrl}
                  alt={el.cameraPageLabel(idx + 1)}
                  className="h-20 w-16 sm:h-24 sm:w-20 object-cover rounded-md border border-white/20 bg-white/5"
                />
                <span className="absolute bottom-1 left-1 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded">
                  {idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removePage(idx)}
                  className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow ring-1 ring-black/30"
                  aria-label={el.cameraPageRemove}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 pb-3 pt-2 bg-black/60 backdrop-blur-sm flex items-center gap-3">
        <button
          disabled={!pages.length}
          onClick={() => removePage(pages.length - 1)}
          className="h-12 w-12 sm:h-14 sm:w-14 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/15 disabled:opacity-30 ring-1 ring-white/20"
          title={el.cameraRemoveLast}
          aria-label={el.cameraRemoveLast}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M10 11v6M14 11v6M5 7l1 13a2 2 0 002 2h8a2 2 0 002-2l1-13M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"/></svg>
        </button>

        <button
          disabled={!ready || capturing}
          onClick={capture}
          className="relative flex-1 max-w-[140px] sm:max-w-[160px] mx-auto h-16 sm:h-18 w-16 sm:w-18 rounded-full bg-white text-blue-700 flex items-center justify-center shadow-2xl ring-4 ring-white/30 hover:scale-[1.03] active:scale-95 transition disabled:opacity-50 disabled:scale-100"
          aria-label={el.cameraCapture}
        >
          <span className="absolute inset-2 rounded-full border-[3px] border-blue-600/80" />
          <span className="absolute inset-4 rounded-full bg-blue-600" />
        </button>

        <button
          disabled={!pages.length}
          onClick={finish}
          className="h-12 sm:h-14 px-4 sm:px-5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
          <span className="hidden sm:inline">{el.cameraDone(pages.length)}</span>
          <span className="sm:hidden">{el.cameraFinish}</span>
        </button>
      </div>
    </div>
  );
}
